import { useEffect, useRef, useState } from "react";
import { TopNav } from "./components/TopNav";
import { useAuth } from "./hooks/useAuth";
import { C } from "./lib/theme";
import { EmptyState } from "./components/EmptyState";
import { Discover } from "./screens/Discover";
import { Stores } from "./screens/Stores";
import { StoreDetail } from "./screens/StoreDetail";
import { Cart } from "./screens/Cart";
import { Orders } from "./screens/Orders";
import { Messages } from "./screens/Messages";
import { SellerDashboard } from "./screens/SellerDashboard";
import { SellerOrders } from "./screens/SellerOrders";
import { SellerProducts } from "./screens/SellerProducts";
import { SellerAnalytics } from "./screens/SellerAnalytics";
import { ProductDetail } from "./screens/ProductDetail";
import { Checkout } from "./screens/Checkout";
import { Profile } from "./screens/Profile";
import { Login } from "./screens/Login";
import { Home } from "./screens/Home";
import { ResetPassword } from "./screens/ResetPassword";
import { SearchModal } from "./components/SearchModal";
import { Search } from "./screens/Search";
import { MFAChallenge } from "./components/MFAChallenge";
import { supabase } from "./lib/supabase";

const CUST_SCREENS = ["discover", "stores", "storeDetail", "productDetail", "search", "cart", "orders", "messages", "profile"];
const SELL_SCREENS = ["dashboard", "sorders", "products", "smessages", "analytics", "profile"];

export function App() {
  const { user, seller, loading, isAuthed, isSeller, isRecovery, refresh } = useAuth();
  // MFA gate — opt-in for sellers + customers (mandatory only for admin role).
  // Three states:
  //   "checking"           → looking up factors / AAL after login
  //   "challenge-required" → has verified factor but session is AAL1
  //   "passed"             → no factor, or already AAL2 — render dashboard
  // If listFactors fails we fail-OPEN (allow access) — opt-in MFA shouldn't
  // lock a customer out of their own cart over a transient network blip. The
  // mandatory-MFA path is admin-only and lives in kapio-admin.
  const [mfaStatus, setMfaStatus] = useState("checking");
  const [mfaFactorId, setMfaFactorId] = useState(null);
  // Public-facing view shown to unauthed visitors. Default to the marketing
  // home page; flip to "login" when the user clicks any sign-in / open-a-store
  // CTA. Resets to "home" if Login is dismissed via the back affordance.
  const [publicView, setPublicView] = useState("home");
  const [role, setRole] = useState("customer");
  const [active, setActive] = useState("discover");
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [prevActive, setPrevActive] = useState("discover");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [messageSellerId, setMessageSellerId] = useState(null);
  const [messageBuyerId, setMessageBuyerId] = useState(null);
  // Carries an order reference set when the user clicks "Message" from an
  // order detail. Messages auto-sends a clickable order-ref card into the
  // thread so both parties always know which order is being discussed.
  const [messageOrderRef, setMessageOrderRef] = useState(null);
  // When set, Orders / SellerOrders auto-select that sub_order on mount —
  // used to deep-link from an order-ref card inside a chat thread.
  const [openSubOrderId, setOpenSubOrderId] = useState(null);
  // Carries initial Search screen config ({ source, query, categoryId? }) set
  // when the user hits the Discover banner search button or picks a section
  // "See all". Consumed by Search on mount.
  const [searchInitial, setSearchInitial] = useState(null);
  // Carries the initial tab to land on inside Profile — set by deep-link
  // entries like the SellerDashboard MFA nudge that wants "Set up 2FA"
  // to land on Privacy & Security in one click.
  const [profileInitialTab, setProfileInitialTab] = useState(null);

  function openProfileTab(tabId) {
    setProfileInitialTab(tabId);
    setActive("profile");
  }

  const openSearch = (initial) => {
    setSearchInitial(initial || { source: "search", query: "" });
    setActive("search");
  };

  function openProduct(p) {
    setSelectedProductId(p.id);
    setPrevActive(active === "productDetail" ? prevActive : active);
    setActive("productDetail");
  }

  // Buyer clicked "Message seller" inside an order detail. Carry the order
  // reference so Messages can auto-send a clickable order-ref card on entry.
  function openMessageWithSeller(sellerId, orderRef = null) {
    setMessageSellerId(sellerId);
    setMessageBuyerId(null);
    setMessageOrderRef(orderRef);
    setRole("customer");
    setActive("messages");
  }

  // Seller clicked "Message customer" inside an order drawer. Mirror image of
  // the buyer flow above.
  function openMessageWithBuyer(buyerId, orderRef = null) {
    setMessageBuyerId(buyerId);
    setMessageSellerId(null);
    setMessageOrderRef(orderRef);
    setRole("seller");
    setActive("smessages");
  }

  // Click on an order-ref card inside a chat — deep-links to the matching
  // sub_order in either Orders (buyer) or SellerOrders (seller).
  function openSubOrderFromChat(subOrderId, asRole) {
    setOpenSubOrderId(subOrderId);
    if (asRole === "seller") {
      setRole("seller");
      setActive("sorders");
    } else {
      setRole("customer");
      setActive("orders");
    }
  }

  useEffect(() => {
    if (isSeller && seller) return; // seller kept whatever they had
    setRole("customer");
  }, [isSeller, seller]);

  // MFA gate — runs whenever the user lands authenticated (and not in
  // recovery mode). Determines whether to:
  //   - Prompt for TOTP / recovery code (factor exists, AAL is aal1)
  //   - Allow dashboard (no factor, or AAL is aal2)
  // Fail-open on listFactors error: opt-in MFA shouldn't lock the user out
  // over a transient network blip. The admin-mandatory path lives elsewhere.
  useEffect(() => {
    if (!isAuthed || isRecovery || !user) {
      setMfaStatus("checking");
      setMfaFactorId(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setMfaStatus("checking");
      try {
        const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        if (fErr) {
          // Fail-open — see comment above.
          console.warn("[desktop] listFactors failed, skipping MFA gate", fErr);
          setMfaStatus("passed");
          return;
        }
        const verified = factors?.totp?.find((f) => f.status === "verified");
        if (!verified) {
          setMfaFactorId(null);
          setMfaStatus("passed");
          return;
        }
        setMfaFactorId(verified.id);
        const { data: aal, error: aErr } =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (aErr) {
          console.warn("[desktop] getAuthenticatorAssuranceLevel failed", aErr);
          // Fail-open — same as above.
          setMfaStatus("passed");
          return;
        }
        setMfaStatus(aal?.currentLevel === "aal2" ? "passed" : "challenge-required");
      } catch (err) {
        if (cancelled) return;
        console.warn("[desktop] MFA check threw, skipping gate", err);
        setMfaStatus("passed");
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthed, isRecovery, user?.id]);

  // After challenge passes (TOTP → AAL2, recovery code → still AAL1 but
  // user has proven possession of a server-issued one-time code) we let the
  // user into the app. For Kapio's threat model — password + a server-issued
  // code — AAL1 is acceptable. Sensitive ops that genuinely require AAL2
  // must check getAuthenticatorAssuranceLevel() at the call site.
  const recheckMfaAfterVerify = () => {
    setMfaStatus("passed");
  };

  const cancelMfaAndSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      /* swallow — auth state listener will handle the rest */
    }
    setMfaStatus("checking");
    setMfaFactorId(null);
  };

  useEffect(() => {
    if (role === "seller" && !SELL_SCREENS.includes(active)) setActive("dashboard");
    if (role === "customer" && !CUST_SCREENS.includes(active)) setActive("discover");
  }, [role]);

  // Clear the deep-link tab on navigation away from Profile — otherwise a
  // stale request would re-trigger the next time the user opens it.
  useEffect(() => {
    if (active !== "profile" && profileInitialTab !== null) {
      setProfileInitialTab(null);
    }
  }, [active, profileInitialTab]);

  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Browser back/forward mapped onto the screen-level state so pressing back
  // from e.g. Search / ProductDetail returns to the previous screen instead
  // of unloading the tab.
  const historyInit = useRef(false);
  const isPopping = useRef(false);
  useEffect(() => {
    if (!historyInit.current) {
      historyInit.current = true;
      window.history.replaceState({ active, role }, "");
      return;
    }
    if (isPopping.current) {
      isPopping.current = false;
      return;
    }
    window.history.pushState({ active, role }, "");
  }, [active, role]);

  useEffect(() => {
    function onPop(e) {
      const s = e.state;
      if (!s || typeof s.active !== "string") return;
      isPopping.current = true;
      if (s.role) setRole(s.role);
      setActive(s.active);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13 }}>
        Loading Kapio…
      </div>
    );
  }

  // Password recovery takes precedence over the rest of the app shell —
  // a user clicking a reset link should land on the new-password screen
  // even though Supabase has technically established a session.
  if (isRecovery) return <ResetPassword />;

  if (!isAuthed) {
    if (publicView === "home") {
      const goToLogin = () => setPublicView("login");
      return (
        <Home
          onSignIn={goToLogin}
          onBrowseStores={goToLogin}
          onViewStore={goToLogin}
          onBecomeSeller={goToLogin}
        />
      );
    }
    return <Login onBackToHome={() => setPublicView("home")} />;
  }

  // ── MFA gate ─────────────────────────────────────────────────────────────
  // Opt-in: shown only when the user has a verified TOTP factor and the
  // current session is AAL1. Otherwise (no factor or already AAL2) we fall
  // through to the dashboard immediately.
  if (mfaStatus === "checking") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13 }}>
        Verifying security…
      </div>
    );
  }
  if (mfaStatus === "challenge-required" && mfaFactorId) {
    return (
      <MFAChallenge
        factorId={mfaFactorId}
        onVerified={recheckMfaAfterVerify}
        onCancel={cancelMfaAndSignOut}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <TopNav
        role={role}
        setRole={setRole}
        active={active}
        setActive={setActive}
        user={user}
        seller={seller}
        onOpenSearch={() => setSearchOpen(true)}
      />
      <main style={{ maxWidth: 1440, margin: "0 auto", padding: "28px 28px 80px" }}>
        {active === "discover" ? (
          <Discover
            user={user}
            setActive={setActive}
            onVisitStore={(s) => {
              setSelectedStore(s);
              setActive("storeDetail");
            }}
            onOpenProduct={openProduct}
            onOpenSearch={openSearch}
          />
        ) : active === "search" ? (
          <Search
            initial={searchInitial}
            user={user}
            onBack={() => setActive("discover")}
            onOpenProduct={openProduct}
            onVisitStore={(s) => {
              setSelectedStore(s);
              setActive("storeDetail");
            }}
          />
        ) : active === "stores" ? (
          <Stores
            onVisit={(s) => {
              setSelectedStore(s);
              setActive("storeDetail");
            }}
          />
        ) : active === "storeDetail" && selectedStore ? (
          <StoreDetail
            store={selectedStore}
            user={user}
            onBack={() => setActive("stores")}
            onOpenProduct={openProduct}
            onOpenMessages={(sid) => {
              setMessageSellerId(sid);
              setActive("messages");
            }}
          />
        ) : active === "productDetail" && selectedProductId ? (
          <ProductDetail
            productId={selectedProductId}
            onBack={() => setActive(prevActive)}
            onOpenProduct={openProduct}
            onVisitStore={(s) => {
              setSelectedStore(s);
              setActive("storeDetail");
            }}
          />
        ) : active === "cart" ? (
          <Cart setActive={setActive} onCheckout={() => setCheckoutOpen(true)} />
        ) : active === "profile" ? (
          <Profile
            user={user}
            seller={seller}
            mode={role}
            setActive={setActive}
            initialTab={profileInitialTab}
            onOpenStore={() => {
              setRole("seller");
              setActive("dashboard");
            }}
            onSellerCreated={async () => {
              await refresh();
              setRole("seller");
              setActive("dashboard");
            }}
            onUserUpdated={refresh}
          />
        ) : active === "orders" ? (
          <Orders
            user={user}
            openSubOrderId={openSubOrderId}
            onOpenedSubOrder={() => setOpenSubOrderId(null)}
            onMessageSeller={openMessageWithSeller}
          />
        ) : active === "messages" ? (
          <Messages
            user={user}
            openSellerId={messageSellerId}
            onOpenedSeller={() => setMessageSellerId(null)}
            orderContext={messageOrderRef}
            onConsumedOrderContext={() => setMessageOrderRef(null)}
            onOpenOrder={(subId) => openSubOrderFromChat(subId, "customer")}
          />
        ) : active === "dashboard" ? (
          <SellerDashboard
            seller={seller}
            setActive={setActive}
            openProfileTab={openProfileTab}
            user={user}
            onSellerCreated={async () => {
              await refresh();
              setRole("seller");
              setActive("dashboard");
            }}
          />
        ) : active === "sorders" ? (
          <SellerOrders
            seller={seller}
            openSubOrderId={openSubOrderId}
            onOpenedSubOrder={() => setOpenSubOrderId(null)}
            onMessageBuyer={openMessageWithBuyer}
          />
        ) : active === "products" ? (
          <SellerProducts seller={seller} />
        ) : active === "smessages" ? (
          <Messages
            user={user}
            seller={seller}
            openBuyerId={messageBuyerId}
            onOpenedBuyer={() => setMessageBuyerId(null)}
            orderContext={messageOrderRef}
            onConsumedOrderContext={() => setMessageOrderRef(null)}
            onOpenOrder={(subId) => openSubOrderFromChat(subId, "seller")}
          />
        ) : active === "analytics" ? (
          <SellerAnalytics seller={seller} />
        ) : (
          <Placeholder screen={active} />
        )}
      </main>
      {checkoutOpen && (
        <Checkout
          user={user}
          onClose={() => setCheckoutOpen(false)}
          onComplete={() => {
            setCheckoutOpen(false);
            setActive("orders");
          }}
        />
      )}
      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpenProduct={openProduct}
        onVisitStore={(s) => {
          setSelectedStore(s);
          setActive("storeDetail");
        }}
      />
    </div>
  );
}

// Temporary — each entry in this list is replaced with a real screen as we build it.
function Placeholder({ screen }) {
  return (
    <div style={{ padding: "80px 0" }}>
      <EmptyState
        icon="hammer"
        title={`${screen} — coming next`}
        body="This screen will be wired to live Supabase data in the next commit."
      />
    </div>
  );
}
